import React, { useContext, useEffect, useState } from 'react';
import { Trans } from '@lingui/macro';
import { useLocation } from 'react-router-dom';
import { useMedia } from '@qover/hooks';
// @ts-ignore
import { ThemeContext } from '@qover/styles';
import { SupportCenterButton } from '../supportCenter';
import { LocaleContext } from '@qover/contexts';
import ArrowIcon from './icons/ArrowIcon';
import { QoverLogo, PartnerLogo } from './TopBarLogos';
import { CountrySelector } from './CountrySelector';
import { LanguageSelector } from './LanguageSelector';
import {
  CompletionBar,
  ComputeButtonRouterLink,
  ComputeButtonLink,
  Content,
  Inner,
  LogoContainer,
  MainLogoContainer,
  RightContainer,
  TopBarContainer,
  PartnershipWith,
  PartnerLogoContainer,
} from './topBar.styles';

const LOGO_STYLES = {
  PARTNER_ONLY: 'partner-logo-only',
  PARTNER_WITH_QOVER: 'partner-logo-with-qover',
  QOVER_WITH_PARTNER: 'qover-logo-with-partner',
};

type LogosProps = {
  draftId?: string;
};

export function Logos(props: LogosProps) {
  const location = useLocation();

  const { locale } = useContext(LocaleContext);
  const theme = useContext<any>(ThemeContext);

  const ls = theme?.featureToggles?.topbar?.logoStyle;
  const landingPageUrl = theme?.featureToggles?.landingPageUrl;
  let mainLogo = null;
  let partnershipLogo = null;
  const draftId =
    new URLSearchParams(location.search).get('id') || props.draftId;
  const landingPageSearch = new URLSearchParams();

  if (draftId) {
    landingPageSearch.set('qover-id', draftId);
  }

  switch (ls) {
    case LOGO_STYLES.PARTNER_WITH_QOVER: {
      mainLogo = <PartnerLogo src={theme?.logo} />;
      partnershipLogo = <QoverLogo />;
      break;
    }
    case LOGO_STYLES.QOVER_WITH_PARTNER: {
      mainLogo = <QoverLogo />;
      partnershipLogo = <PartnerLogo src={theme?.logo} />;
      break;
    }
    case LOGO_STYLES.PARTNER_ONLY: {
      mainLogo = <PartnerLogo src={theme?.logo} />;
      partnershipLogo = null;
      break;
    }
    default: {
      break;
    }
  }

  return (
    <LogoContainer>
      <MainLogoContainer>
        {landingPageUrl?.[locale.value] ? (
          <a
            href={`${
              theme?.featureToggles?.landingPageUrl?.[locale.value]
            }?${landingPageSearch.toString()}`}
            title="Landing page"
          >
            {mainLogo}
          </a>
        ) : (
          mainLogo
        )}
      </MainLogoContainer>
      {ls !== LOGO_STYLES.PARTNER_ONLY && (
        <PartnershipWith>
          <Trans id="general.inPartnershipWith" />
          <PartnerLogoContainer>{partnershipLogo}</PartnerLogoContainer>
        </PartnershipWith>
      )}
    </LogoContainer>
  );
}

type InnerContentProps = {
  completionPercentage?: number;
  showComputeButton?: boolean;
  showLanguageSelector?: boolean;
  computeButtonTo?: string;
  onComputeButtonClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  showChat?: () => void;
};

function InnerContent(props: InnerContentProps) {
  const defaultProps = {
    'data-test': 'topbar-compute-button',
    onClick: props.onComputeButtonClick,
    renderBefore: ArrowIcon,
    children: <Trans id="general.topBar.computeButton" />,
  };

  const isMobile = useMedia(['(max-width: 767px)'], [true], false);

  return (
    <>
      {props.completionPercentage != null && (
        <CompletionBar completionPercentage={props.completionPercentage} />
      )}
      <Content>
        <Logos />
        <RightContainer>
          <SupportCenterButton showChat={props.showChat} />
          {props.showLanguageSelector && <LanguageSelector />}
          {props.showComputeButton && props.computeButtonTo && !isMobile && (
            <ComputeButtonRouterLink
              {...defaultProps}
              to={props.computeButtonTo}
            />
          )}
          {props.showComputeButton && !props.computeButtonTo && !isMobile && (
            <ComputeButtonLink {...defaultProps} href="#computeOffer" />
          )}
        </RightContainer>
      </Content>
    </>
  );
}

type TopBarProps = React.PropsWithChildren<{
  completionPercentage?: number;
  fixed?: boolean;
  flowStartUrl?: string;
  onComputeButtonClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  showChat?: () => void;
  showComputeButton?: boolean;
  showCountrySelector?: boolean;
  showCountrySelectorModal?: boolean;
  showLanguageSelector?: boolean;
  updateDraftCountry?: (locale: string) => Promise<any>;
}>;

export function TopBar(props: TopBarProps) {
  const { flowStartUrl, showCountrySelector, ...innerProps } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <TopBarContainer
      id="topbar-container"
      className={props.fixed ? 'sticky' : ''}
    >
      {props.children}
      {showCountrySelector && flowStartUrl && mounted && (
        <CountrySelector
          flowStartUrl={flowStartUrl}
          updateDraftCountry={props.updateDraftCountry}
          showCountrySelectorModal={props.showCountrySelectorModal}
        />
      )}
      <Inner>
        {/* we use the fixed props because it's only used on the landing page */}
        <InnerContent {...innerProps} />
      </Inner>
    </TopBarContainer>
  );
}

TopBar.displayName = 'TopBar';
